import { teamRoleResponseSchema } from '@tempo-io/capacity-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import type { TeamRole, TeamRoleWithoutCounts } from '@/common/api/types/teamRoles';

const teamRolesResponseSchema = z.array(teamRoleResponseSchema);

/**
 * The read endpoints are validated because callers depend on the aggregate counts being present -
 * the delete-role dialog decides whether to block on `openPositionCount`, so an absent field must
 * fail loudly rather than read as zero.
 *
 * The mutating endpoints deliberately are not. Nothing reads their body beyond the fields being
 * cached, and by the time a response is parsed the write has already committed - a schema mismatch
 * would surface to the user as a failed rename that actually persisted.
 */
const getTeamRoles = async (activeOnly?: boolean): Promise<TeamRole[]> => {
  return teamRolesResponseSchema.parse(
    await tempoApi()
      .get('api/v1/capacity/team-roles', {
        ...(activeOnly !== undefined && { searchParams: { activeOnly } }),
      })
      .json<unknown>(),
  );
};

const updateTeamRole = async (
  id: number,
  fields: { name?: string; isCritical?: boolean; isDefault?: boolean },
): Promise<TeamRoleWithoutCounts> => {
  return await tempoApi()
    .patch(`api/v1/capacity/team-roles/${id}`, { json: fields })
    .json<TeamRoleWithoutCounts>();
};

const searchTeamRoles = async (query: string, activeOnly?: boolean): Promise<TeamRole[]> => {
  return teamRolesResponseSchema.parse(
    await tempoApi()
      .get('api/v1/capacity/team-roles/search', {
        searchParams: {
          query,
          ...(activeOnly !== undefined && { activeOnly }),
        },
      })
      .json<unknown>(),
  );
};

const bulkCreateTeamRoles = async (
  teamRoles: Array<{ name: string; isCritical?: boolean; isDefault?: boolean }>,
): Promise<TeamRoleWithoutCounts[]> => {
  return await tempoApi()
    .post('api/v1/capacity/team-roles/bulk', {
      json: {
        roles: teamRoles,
      },
    })
    .json<TeamRoleWithoutCounts[]>();
};

const deleteTeamRole = async (id: number): Promise<void> => {
  await tempoApi().delete(`api/v1/capacity/team-roles/${id}`);
};

const archiveTeamRole = async (id: number): Promise<TeamRoleWithoutCounts> => {
  return await tempoApi()
    .patch(`api/v1/capacity/team-roles/${id}/archive`)
    .json<TeamRoleWithoutCounts>();
};

const unarchiveTeamRole = async (id: number): Promise<TeamRoleWithoutCounts> => {
  return await tempoApi()
    .patch(`api/v1/capacity/team-roles/${id}/unarchive`)
    .json<TeamRoleWithoutCounts>();
};

export const TeamRolesApi = {
  getTeamRoles,
  searchTeamRoles,
  bulkCreateTeamRoles,
  updateTeamRole,
  deleteTeamRole,
  archiveTeamRole,
  unarchiveTeamRole,
} as const;
